// Seedable PRNG for deterministic dice rolls.
//
// Algorithm: Mulberry32 — a fast, high-quality 32-bit PRNG by Tommy Ettinger.
// Reference: https://gist.github.com/tommyettinger/46a874533244883189143505d203312c
//
// State is a single uint32 advanced with each call to next().  The seed is
// stored as a decimal string so it survives JSON / Prisma text round-trips.

import { DEFAULT_DICE_SIDES } from '@warcarp/api-types'

export interface Rng {
  /** Returns a float in [0, 1). */
  next(): number
  /** Returns the current seed as a string (call after next() to get the advanced state). */
  nextSeed(): string
}

/**
 * Create a seedable RNG from a string seed.
 *
 * The seed string is parsed as a base-10 unsigned 32-bit integer.
 * Use `nextSeed()` to capture the current state for persistence.
 */
export function createRng(seed: string): Rng {
  // Parse to uint32; parseInt may produce a negative signed int for large values.
  let state = (parseInt(seed, 10) | 0) >>> 0

  return {
    next(): number {
      // Mulberry32 step: advance state, then mix.
      state = (state + 0x6d2b79f5) >>> 0
      let z = state
      z = Math.imul(z ^ (z >>> 15), z | 1)
      z ^= z + Math.imul(z ^ (z >>> 7), z | 61)
      return ((z ^ (z >>> 14)) >>> 0) / 0x100000000
    },
    nextSeed(): string {
      return state.toString(10)
    },
  }
}

/**
 * Roll `n` dice, each producing a value in [1, `sides`].
 * Returns the array sorted **descending** (highest first).
 *
 * `sides` defaults to six. Maps can raise it per border side (see
 * `Territory.borderDice`), which is how Capture the Flag's towers roll d8.
 */
export function rollDice(rng: Rng, n: number, sides: number = DEFAULT_DICE_SIDES): number[] {
  const rolls: number[] = []
  for (let i = 0; i < n; i++) {
    rolls.push(Math.floor(rng.next() * sides) + 1)
  }
  return rolls.sort((a, b) => b - a)
}
